import { useState, useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  type ColumnDef,
} from '@tanstack/react-table'
import { Search, Building2, Check, ChevronsUpDown } from 'lucide-react'
import { usePhenotypes } from '@/hooks/use-phenotypes'
import { useOrganization } from '@/contexts/organization-context'
import { DataTable } from '@/components/data-table/DataTable'
import { DataTablePagination } from '@/components/data-table/DataTablePagination'
import { PhenotypeCard } from '@/components/phenotypes/PhenotypeCard'
import { ViewToggle } from '@/components/phenotypes/ViewToggle'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { Phenotype } from '@/api/types'

export function Phenotypes() {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const [globalFilter, setGlobalFilter] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [orgPopoverOpen, setOrgPopoverOpen] = useState(false)

  const { selectedOrgId, selectedOrg, organizations, setSelectedOrgId } = useOrganization()

  // Filter organizations by search term
  const filteredOrgs = useMemo(() => {
    if (!organizations.length) return []
    if (!orgSearch) return organizations
    const q = orgSearch.toLowerCase()
    return organizations.filter(o =>
      o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q)
    )
  }, [organizations, orgSearch])

  // Only fetch phenotypes once an org is selected (avoids wasted initial fetch)
  const { data: phenotypes, isLoading, isError } = usePhenotypes(selectedOrgId || undefined)

  // Stable reference for table data — avoids infinite re-render loop when phenotypes is undefined
  const tableData = useMemo(() => phenotypes ?? [], [phenotypes])

  // Column definitions for table view
  const columns = useMemo<ColumnDef<Phenotype>[]>(
    () => [
      {
        accessorKey: 'carrierName',
        header: 'Carrier',
        cell: ({ row }) => row.original.carrierName,
      },
      {
        accessorKey: 'id',
        header: 'Phenotype ID',
        cell: ({ row }) => {
          const id = row.original.id
          const displayId = id.length > 8 ? `${id.slice(0, 8)}...` : id
          return <span className="font-mono text-xs">{displayId}</span>
        },
      },
      {
        accessorKey: 'detectionThreshold',
        header: 'Threshold',
        cell: ({ row }) => {
          const threshold = row.original.detectionThreshold
          return `${Math.round(threshold * 100)}%`
        },
      },
      {
        accessorKey: 'hasExtractor',
        header: 'Status',
        cell: ({ row }) => {
          const { hasExtractor, lastUpdated } = row.original
          if (hasExtractor) {
            return <Badge variant="success">Has Extractor</Badge>
          }
          // Check if lastUpdated is within the last 7 days
          const isRecent = lastUpdated &&
            new Date(lastUpdated) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          if (isRecent) {
            return <Badge variant="warning">Pending</Badge>
          }
          return <Badge variant="secondary">No Extractor</Badge>
        },
      },
    ],
    []
  )

  // Configure TanStack Table
  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: viewMode === 'grid' ? 9 : 20,
      },
    },
  })

  // Error state
  if (isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Phenotypes</h1>
        <div className="rounded-md border border-destructive p-4 text-destructive">
          Failed to load phenotypes. Please try refreshing the page.
        </div>
      </div>
    )
  }

  // Calculate phenotype count
  const phenotypeCount = phenotypes?.length ?? 0
  const filteredCount = table.getFilteredRowModel().rows.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Phenotypes</h1>
        <p className="text-muted-foreground">
          {phenotypeCount > 0
            ? `${phenotypeCount} phenotype${phenotypeCount === 1 ? '' : 's'} configured`
            : 'No phenotypes configured'}
          {selectedOrg ? ` for ${selectedOrg.name}` : ''}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Organization selector */}
          {organizations.length > 0 && (
            <Popover open={orgPopoverOpen} onOpenChange={(open) => {
              setOrgPopoverOpen(open)
              if (!open) setOrgSearch('')
            }}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "flex h-9 w-[260px] items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring",
                    !selectedOrgId && "text-muted-foreground"
                  )}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      {selectedOrg?.name || 'Select organization'}
                    </span>
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search organizations..."
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    className="h-8"
                    autoFocus
                  />
                </div>
                <div className="max-h-[300px] overflow-y-auto p-1">
                  {filteredOrgs.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      No organizations found.
                    </p>
                  ) : (
                    filteredOrgs.map(org => (
                      <button
                        key={org.id}
                        className={cn(
                          "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                          org.id === selectedOrgId && "bg-accent"
                        )}
                        onClick={() => {
                          setSelectedOrgId(org.id)
                          setOrgPopoverOpen(false)
                          setOrgSearch('')
                        }}
                      >
                        {org.id === selectedOrgId && (
                          <Check className="absolute left-2 h-4 w-4" />
                        )}
                        <span className="truncate">{org.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search carriers..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      </div>

      {/* Content */}
      {isLoading ? (
        // Loading skeleton
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-40 bg-muted" />
            </Card>
          ))}
        </div>
      ) : filteredCount === 0 ? (
        // Empty state
        <div className="rounded-md border border-dashed p-12 text-center">
          <p className="text-muted-foreground">
            {globalFilter
              ? 'No phenotypes found matching your search.'
              : 'No phenotypes configured yet.'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        // Grid view
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {table.getRowModel().rows.map((row) => (
            <PhenotypeCard key={row.id} phenotype={row.original} />
          ))}
        </div>
      ) : (
        // Table view
        <DataTable table={table} isLoading={isLoading} />
      )}

      {/* Pagination */}
      {filteredCount > 0 && <DataTablePagination table={table} />}
    </div>
  )
}
